/**
 * Chat API utilities for internal clinic communication
 */

import { API_BASE_URL } from '../config/api';

// ============================================
// Types
// ============================================

/**
 * Rich card data for sharing resources like pets, encounters, appointments, etc.
 */
export interface CardData {
  type: 'pet' | 'encounter' | 'appointment' | 'task';  // Extensible for future types
  title: string;
  subtitle?: string;
  image_url?: string;
  link: string;
  tags?: string[];
  metadata?: Record<string, string>;
}

/**
 * Attachment data for file/image uploads in chat
 */
export interface AttachmentData {
  type: 'image' | 'file';
  filename: string;
  content_type: string;
  size_bytes: number;
  url: string;
  s3_key: string;
  file_id: string;
}

/**
 * MMS media attachment data
 */
export interface MMSAttachment {
  url: string;
  content_type: string;
  s3_key?: string;
  filename?: string;
  size?: number;
  is_image?: boolean;
  is_video?: boolean;
}

export interface ChatMessage {
  message_id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_photo_url?: string;
  recipient_id: string;
  message_text: string;
  created_at: number;
  message_type: string;  // 'text', 'sms', 'mms', 'card', 'attachment'
  read_by: string[];
  edited_at?: number;  // Timestamp when message was edited
  card_data?: CardData;  // Rich card data for card message type
  attachment_data?: AttachmentData;  // File/image attachment data
  reactions?: Record<string, string[]>; // emoji -> user_ids
  // External SMS/MMS message fields
  is_external?: boolean;
  external_phone?: string;
  external_name?: string;
  external_pet_owner_id?: string;
  direction?: 'inbound' | 'outbound';
  media_attachments?: MMSAttachment[];  // MMS media attachments
}

export interface Conversation {
  conversation_id: string;
  conversation_type: 'broadcast' | 'dm' | 'sms_external';
  participant_ids: string[];
  participant_names: string[];
  participant_photo_urls?: string[];
  last_message: string | null;
  last_message_at: number | null;
  unread_count: number;
  // External SMS conversation fields
  is_external?: boolean;
  external_name?: string;
  external_phone?: string;
  external_pet_owner_id?: string;
  external_photo_url?: string;
}

export interface TeamMember {
  id: string;
  full_name: string;
  role: string;
  is_online: boolean;
  photo_url?: string;
}

export interface SendMessageRequest {
  recipient_id: string;
  message_text: string;
  message_type?: string;
  card_data?: CardData;
  attachment_data?: AttachmentData;
}

export interface SendMessageResponse {
  message_id: string;
  status: string;
  timestamp: number;
}

export interface GetMessagesParams {
  conversationId?: string;
  recipientId?: string;
  limit?: number;
  before?: number;
}

export interface WebSocketUrlResponse {
  websocket_url: string;
  instructions: string;
}

export interface ReactToMessageRequest {
  emoji: string;
  action?: 'add' | 'remove';
}

export interface ReactToMessageResponse {
  status: string;
  timestamp: number;
}

// ============================================
// API Functions
// ============================================

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

/**
 * Send a chat message to a team member or broadcast to the entire practice
 */
export const sendMessage = async (request: SendMessageRequest): Promise<SendMessageResponse> => {
  const body: Record<string, unknown> = {
    recipient_id: request.recipient_id,
    message_text: request.message_text,
    message_type: request.message_type || 'text',
  };
  
  // Include card_data if present
  if (request.card_data) {
    body.card_data = request.card_data;
  }
  
  // Include attachment_data if present
  if (request.attachment_data) {
    body.attachment_data = request.attachment_data;
  }
  
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse<SendMessageResponse>(response);
};

/**
 * Get chat message history
 */
export const getMessages = async (params: GetMessagesParams = {}): Promise<ChatMessage[]> => {
  const searchParams = new URLSearchParams();
  if (params.conversationId) searchParams.append('conversation_id', params.conversationId);
  if (params.recipientId) searchParams.append('recipient_id', params.recipientId);
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.before) searchParams.append('before', params.before.toString());

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/v1/chat/messages${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ChatMessage[]>(response);
};

/**
 * Get list of conversations for the current user
 */
export const getConversations = async (): Promise<Conversation[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/conversations`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<Conversation[]>(response);
};

/**
 * Get team members available for chat
 */
export const getTeamMembers = async (): Promise<TeamMember[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/team`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<TeamMember[]>(response);
};

/**
 * Mark a message as read
 */
export const markMessageRead = async (messageId: string): Promise<void> => {
  await fetch(`${API_BASE_URL}/api/v1/chat/messages/${messageId}/read`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
};

/**
 * Mark all messages in a conversation as read
 */
export const markConversationRead = async (conversationId: string): Promise<void> => {
  await fetch(`${API_BASE_URL}/api/v1/chat/conversations/${encodeURIComponent(conversationId)}/read`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
};

/**
 * Delete (hide) a conversation from the user's view
 */
export const deleteConversation = async (conversationId: string): Promise<void> => {
  await fetch(`${API_BASE_URL}/api/v1/chat/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
};

/**
 * Delete a single message (hard delete for everyone)
 * Only the sender can delete their own messages
 */
export const deleteMessage = async (messageId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/messages/${encodeURIComponent(messageId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete message' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
};

/**
 * Edit a message (only sender can edit)
 * Returns the updated message with edited_at timestamp
 */
export const editMessage = async (messageId: string, newText: string): Promise<ChatMessage> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/messages/${encodeURIComponent(messageId)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ message_text: newText }),
  });
  return handleResponse<ChatMessage>(response);
};

/**
 * React to a message with an emoji (add/remove)
 */
export const reactToMessage = async (
  messageId: string,
  request: ReactToMessageRequest
): Promise<ReactToMessageResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<ReactToMessageResponse>(response);
};

/**
 * Get WebSocket URL for real-time updates
 */
export const getWebSocketUrl = async (): Promise<WebSocketUrlResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/websocket-url`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<WebSocketUrlResponse>(response);
};

// ============================================
// Chat Attachment Upload Functions
// ============================================

export interface AttachmentUploadInitiateRequest {
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface AttachmentUploadInitiateResponse {
  upload_url: string;
  fields: Record<string, string>;
  s3_key: string;
  file_id: string;
}

export interface AttachmentUploadCompleteRequest {
  s3_key: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  file_id: string;
}

export interface AttachmentUploadCompleteResponse {
  url: string;
  s3_key: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  file_id: string;
}

/**
 * Initiate a chat attachment upload - gets presigned S3 URL
 */
export const initiateAttachmentUpload = async (
  request: AttachmentUploadInitiateRequest
): Promise<AttachmentUploadInitiateResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/attachments/initiate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<AttachmentUploadInitiateResponse>(response);
};

/**
 * Upload a file to S3 using presigned URL
 */
export const uploadFileToS3 = async (
  uploadUrl: string,
  fields: Record<string, string>,
  file: File
): Promise<void> => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  const response = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to S3');
  }
};

/**
 * Complete a chat attachment upload - gets signed download URL
 */
export const completeAttachmentUpload = async (
  request: AttachmentUploadCompleteRequest
): Promise<AttachmentUploadCompleteResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/attachments/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<AttachmentUploadCompleteResponse>(response);
};

export interface RefreshAttachmentUrlResponse {
  url: string;
  s3_key: string;
  expires_in: number;
}

/**
 * Refresh a presigned URL for an attachment that may have expired.
 * Call this when an image fails to load due to an expired presigned URL.
 */
export const refreshAttachmentUrl = async (s3Key: string): Promise<RefreshAttachmentUrlResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/attachments/refresh-url`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ s3_key: s3Key }),
  });
  return handleResponse<RefreshAttachmentUrlResponse>(response);
};

// ============================================
// SMS/MMS Reply Functions
// ============================================

export interface MediaAttachmentRequest {
  url: string;
  content_type: string;
  s3_key?: string;
  filename?: string;
}

export interface SendSMSReplyRequest {
  message_text: string;
  media_attachments?: MediaAttachmentRequest[];
}

export interface SendSMSReplyResponse {
  success: boolean;
  message_sid?: string;
  error?: string;
  is_mms?: boolean;
}

/**
 * Send an SMS or MMS reply to a pet owner
 * If media_attachments are provided, sends as MMS
 */
export const sendSMSReply = async (
  petOwnerId: string,
  request: SendSMSReplyRequest
): Promise<SendSMSReplyResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/chat/sms/${petOwnerId}/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to send SMS/MMS' }));
    throw new Error(error.detail || 'Failed to send SMS/MMS');
  }

  return response.json();
};

/**
 * Upload a file attachment and return attachment data ready for sending
 */
export const uploadChatAttachment = async (file: File): Promise<AttachmentData> => {
  // 1. Initiate upload
  const initResponse = await initiateAttachmentUpload({
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
  });

  // 2. Upload to S3
  await uploadFileToS3(initResponse.upload_url, initResponse.fields, file);

  // 3. Complete upload
  const completeResponse = await completeAttachmentUpload({
    s3_key: initResponse.s3_key,
    filename: file.name,
    content_type: file.type,
    size_bytes: file.size,
    file_id: initResponse.file_id,
  });

  // 4. Return attachment data
  const isImage = file.type.startsWith('image/');
  return {
    type: isImage ? 'image' : 'file',
    filename: completeResponse.filename,
    content_type: completeResponse.content_type,
    size_bytes: completeResponse.size_bytes,
    url: completeResponse.url,
    s3_key: completeResponse.s3_key,
    file_id: completeResponse.file_id,
  };
};

// Export as a namespace for cleaner imports
export const chatApi = {
  sendMessage,
  getMessages,
  getConversations,
  getTeamMembers,
  markMessageRead,
  markConversationRead,
  deleteConversation,
  deleteMessage,
  editMessage,
  reactToMessage,
  getWebSocketUrl,
  initiateAttachmentUpload,
  uploadFileToS3,
  completeAttachmentUpload,
  uploadChatAttachment,
  sendSMSReply,
};

export default chatApi;

